import {Action, Selector, State, StateContext, Store} from '@ngxs/store';
import {HttpClient} from '@angular/common/http';
import {catchError, mergeMap, tap} from 'rxjs/operators';
import {Injectable} from '@angular/core';
import * as Act from './profile.actions';
import {Observable, of} from 'rxjs';

import {userNotificationsMock} from '../../../../tests/mocks/userNotificationsMock';
import {endpoints} from '../../constants/endpoints';
import {SetActiveUser, UpdateUserAccount, UserHasPin} from '../auth/auth.actions';
import {AuthState} from '../auth/auth.state';
import {ITeamRecord} from '../../../team/interfaces/team-record.interface';
import {IWholesalerConnection} from '../../interfaces/wholesaler-integration.interface';
import {STATES_LIST} from '../../constants/states';
import {IWholesaler} from '../../interfaces/wholesaler.interface';
import {PharmacyState} from '../pharmacy/pharmacy.state';
import {RunningResultEnum} from '../../interfaces/RunningResultEnum';
// eslint-disable-next-line max-len
import { IIntegration, IIntegrationConfiguration, IntegrationTypeEnum } from '../../interfaces/integration.interface';
import { HttpStatusCode } from '../../interfaces/HttpStatusCode';
import { IResponse } from '../../interfaces/IResponse';
import { IUser } from '../../interfaces/user.interface';

export interface ProfileStateModel {
  loading: boolean;
  runningWholesaler: IWholesalerConnection;
  runningResult: RunningResultEnum;
  success: boolean;
  customerId: string;
  signerId: string;
  states: string[];
  notifications: any;
  failedInvitationEmails: string[];
  teamList: ITeamRecord[];
  pharmacyManagementSystemIntegration: IIntegration;
  pharmacyManagementSystemIntegrations: IIntegrationConfiguration[];
  wholesalerIntegrations: IWholesalerConnection[];
  witnessError: boolean;
  editUserResponse: any;
  dashboardData: any;
  wholesalers: IWholesaler[];
  wholesalersFullList: IWholesaler[];
  currentPharmacyAvailableWholesalersList: IWholesaler[];
  pharmacySelectedWholesaler: IWholesalerConnection;
  testPharmacyManagementSystemConnectionResponseStatusCode: HttpStatusCode;
  wholesalerCredentials: any,
  response: IResponse,
  amendingUserId: string;
  witnessUserId: string;
  beaconSignature: string;
}

@State<ProfileStateModel>({
  name: 'profile',
})

@Injectable()
export class ProfileState {
  pinUserIDInner: string;

  get pinUserID(): string {
    return this.pinUserIDInner;
  }

  set pinUserID(v: string) {
    this.pinUserIDInner = v.slice();
  }

  constructor(
    private http: HttpClient,
    private store: Store
  ) {
  }

  @Selector()
  static loading(state: ProfileStateModel): boolean {
    return state.loading;
  }

  @Selector()
  static runningWholesaler(state: ProfileStateModel): IWholesalerConnection {
    return state.runningWholesaler;
  }

  @Selector()
  static runningResult(state: ProfileStateModel): RunningResultEnum {
    return state.runningResult;
  }

  @Selector()
  static success(state: ProfileStateModel): boolean {
    return state.success;
  }

  @Selector()
  static failedInvitationEmails(state: ProfileStateModel): string[] {
    return state.failedInvitationEmails;
  }

  @Selector()
  static witnessError(state: ProfileStateModel): boolean {
    return state.witnessError;
  }

  @Selector()
  static signerId(state: ProfileStateModel): string {
    return state.signerId;
  }

  @Selector()
  static states(state: ProfileStateModel): string[] {
    return state.states;
  }

  @Selector()
  static notifications(state: ProfileStateModel): any {
    return state.notifications;
  }

  @Selector()
  static teamList(state: ProfileStateModel): ITeamRecord[] {
    return state.teamList;
  }

  @Selector()
  static pharmacyManagementSystemIntegration(state: ProfileStateModel): IIntegrationConfiguration {
    return state.pharmacyManagementSystemIntegrations?.find(pms => pms.isActive);
  }

  @Selector()
  static pharmacyManagementSystemIntegrations(state: ProfileStateModel): IIntegrationConfiguration[] {
    return state.pharmacyManagementSystemIntegrations;
  }

  @Selector()
  static wholesalerIntegrations(state: ProfileStateModel): IWholesalerConnection[] {
    return state.wholesalerIntegrations;
  }

  @Selector()
  static wholesalers(state: ProfileStateModel): IWholesaler[] {
    return state.wholesalers;
  }

  @Selector()
  static editUserResponse(state: ProfileStateModel): any {
    return state.editUserResponse;
  }

  @Selector()
  static dashboardData(state: ProfileStateModel): any {
    return state.dashboardData;
  }

  @Selector()
  static currentPharmacyAvailableWholesalersList(state: ProfileStateModel): IWholesaler[] {
    return state.currentPharmacyAvailableWholesalersList;
  }

  @Selector()
  static testPharmacyManagementSystemConnectionResponseStatusCode(state: ProfileStateModel): HttpStatusCode {
    return state.testPharmacyManagementSystemConnectionResponseStatusCode;
  }

  @Selector()
  static wholesalersFullList(state: ProfileStateModel): IWholesaler[] {
    return state.wholesalersFullList;
  }

  @Selector()
  static beaconSignature(state: ProfileStateModel): string {
    return state.beaconSignature;
  }


  @Action(Act.GetUserTeamList)
  getUserTeamList(ctx: StateContext<ProfileStateModel>, {status = ''}: Act.GetUserTeamList): Observable<any> {
    const pharmacyId = this.store.selectSnapshot(AuthState.pharmacyId);
    if (!pharmacyId || pharmacyId === 'undefined') {
      return;
    }
    ctx.patchState({loading: true, teamList: []});
    return this.http.get(endpoints.pharmacy.getUsersByPharmacyId + pharmacyId + '/' + status)
      .pipe(
        tap((resp: any) => {
          ctx.patchState({
            teamList: resp.value?.users || [],
            loading: false,
          });
        }),
        catchError((err) => {
          ctx.patchState({loading: false});
          return of(null);
        }),
      );
  }

  @Action(Act.SetUserTeamList)
  setUserTeamList(ctx: StateContext<ProfileStateModel>, {teamList}: Act.SetUserTeamList): void {
    ctx.patchState({teamList: teamList || []});
  }

  @Action(Act.ResetProfileSuccess)
  resetProfileSuccess(ctx: StateContext<ProfileStateModel>, {}: Act.ResetProfileSuccess): void {
    ctx.patchState({success: null});
  }

  @Action(Act.GetUserNotifications)
  getUserNotifications(ctx: StateContext<ProfileStateModel>, {customerId}: Act.GetUserNotifications): Observable<any> {
    ctx.patchState({loading: true, notifications: {}});

    // TODO: replace with real endpoint
    // return this.http.get(environment.production + `?customerId=${customerId}`)
    return of({})
      .pipe(
        tap((resp: any) => {
          ctx.patchState({
            notifications: userNotificationsMock,
            loading: false,
          });
        }),
        catchError((err) => {
          ctx.patchState({loading: false});
          return of(null);
        }),
      );
  }

  @Action(Act.LoadMoreNotifications)
  loadMoreNotifications(ctx: StateContext<ProfileStateModel>, {}: Act.LoadMoreNotifications): Observable<any> {
    ctx.patchState({loading: true, notifications: {}});

    // TODO: replace with real endpoint
    // return this.http.get(environment.production + `?customerId=${customerId}`)
    return of({})
      .pipe(
        tap((resp: any) => {
          ctx.patchState({
            notifications: userNotificationsMock,
            loading: false,
          });
        }),
        catchError((err) => {
          ctx.patchState({loading: false});
          return of(null);
        }),
      );
  }

  @Action(Act.ClearAllNotifications)
  clearAllNotifications(ctx: StateContext<ProfileStateModel>, {}: Act.ClearAllNotifications): Observable<any> {
    ctx.patchState({loading: true});

    // TODO: replace with real endpoint
    // return this.http.get(environment.production + `?customerId=${customerId}`)
    return of({})
      .pipe(
        tap((resp: any) => {
          ctx.patchState({
            notifications: {
              items: [],
              count: 0,
            },
            loading: false,
          });
        }),
        catchError((err) => {
          ctx.patchState({loading: false});
          return of(null);
        }),
      );
  }

  @Action(Act.GetStates)
  getStates(ctx: StateContext<ProfileStateModel>, {}: Act.GetStates): void {
    ctx.patchState({states: STATES_LIST});
  }

  @Action(Act.GetDashboardInfo)
  getDashboardInfo(ctx: StateContext<ProfileStateModel>, {}: Act.GetDashboardInfo): Observable<any> {
    // TODO: save in localstorage when all checkpoints are ready
    const pharmacyId = this.store.selectSnapshot(AuthState.pharmacyId);

    ctx.patchState({loading: true, dashboardData: null});
    return this.http.get(endpoints.pharmacy.getDashboardData + `?id=${pharmacyId}`)
      .pipe(
        tap((responseData: any) => {
          ctx.patchState({
            dashboardData: responseData?.value,
            loading: false,
          });
        }),
        catchError((err) => {
          ctx.patchState({loading: false});
          return of(null);
        }),
      );
  }

  @Action(Act.GetPharmacyManagementSystems)
  getPharmacyManagementSystems(ctx: StateContext<ProfileStateModel>, {}:
    Act.GetPharmacyManagementSystems): Observable<any> {
    const pharmacyId = this.store.selectSnapshot(PharmacyState.currentPharmacy)?.id;
    if (!pharmacyId)
      return;
    ctx.patchState({loading: true, dashboardData: null});
    return this.http.get(endpoints.integration.getAll + `/${pharmacyId}`)
      .pipe(
        tap((responseData: any) => {
          ctx.patchState({
            pharmacyManagementSystemIntegrations: responseData?.value,
            loading: false,
          });
        }),
        catchError((err) => {
          ctx.patchState({loading: false});
          return of(null);
        }),
      );
  }

  @Action(Act.SetPharmacyManagementSystemIntegrationConnectionStatus)
  setPharmacyManagementSystemIntegrationConnectionStatus(ctx: StateContext<ProfileStateModel>, {id, setActive}:
    Act.SetPharmacyManagementSystemIntegrationConnectionStatus): Observable<any> {
    const pharmacyId = this.store.selectSnapshot(PharmacyState.currentPharmacy)?.id;
    ctx.patchState({loading: true});
    return this.http.post(endpoints.integration.toggle, {id, pharmacyId})
      .pipe(
        tap((resp: any) => {
          ctx.patchState({
            loading: false,
            pharmacyManagementSystemIntegrations:
              ctx.getState().pharmacyManagementSystemIntegrations.map(x => (x.id === id ? {...x, isActive: setActive} : x))
          });
        }),
        catchError((err) => {
          ctx.patchState({loading: false});
          return of(null);
        }),
      );
  }

  @Action(Act.EditWholesalerConnection)
  editWholesalerConnection(ctx: StateContext<ProfileStateModel>, {connectionData}: Act.EditWholesalerConnection): Observable<any> {
    ctx.patchState({loading: true});
    const wholesalerIntegrations = [...ctx.getState().wholesalerIntegrations];
    const idx = wholesalerIntegrations.findIndex(x => x.id === connectionData?.id);
    if (idx > -1) {
      wholesalerIntegrations[idx] = connectionData as IWholesalerConnection;
      wholesalerIntegrations[idx].wholesalerName = ctx.getState().wholesalers.find(x => x.id === connectionData.wholesalerId).name;
    }
    return this.http.patch(endpoints.wholesalerIntegration.edit, connectionData)
      .pipe(
        tap((responseData: any) => {
          ctx.patchState({
            loading: false,
            success: responseData.success,
            wholesalerIntegrations,
          });
        }),
        catchError((err) => {
          ctx.patchState({loading: false});
          return of(null);
        }),
      );
  }

  @Action(Act.RemoveWholesalerConnection)
  removeWholesalerConnection(ctx: StateContext<ProfileStateModel>, {id}: Act.RemoveWholesalerConnection): Observable<any> {
    ctx.patchState({loading: true});

    return this.http.delete(endpoints.wholesalerIntegration.delete + '?id=' + id)
      .pipe(
        tap((responseData: any) => {
          ctx.patchState({
            loading: false,
            success: responseData.success,
            wholesalerIntegrations: ctx.getState().wholesalerIntegrations.filter(x => x?.id !== id)
          });
        }),
        catchError((err) => {
          ctx.patchState({loading: false});
          return of(null);
        }),
      );
  }

  @Action(Act.GetAllWholesalerConnections)
  getAllWholesalerConnections(ctx: StateContext<ProfileStateModel>, {}: Act.GetAllWholesalerConnections): Observable<any> {
    const pharmacyId = this.store.selectSnapshot(PharmacyState.currentPharmacy)?.id;
    ctx.patchState({loading: true});
    if (pharmacyId === undefined) {
      return;
    }
    return this.http.get(endpoints.wholesalerIntegration.getAll + '?id=' + pharmacyId)
      .pipe(
        tap((responseData: any) => {
          ctx.patchState({
            loading: false,
            success: responseData.success,
            wholesalerIntegrations: responseData?.value
          });
        }),
        catchError((err) => {
          ctx.patchState({loading: false});
          return of(null);
        }),
      );
  }


  @Action(Act.GetWholesalersList)
  getWholesalersList(ctx: StateContext<ProfileStateModel>, {limitToPharmacy}: Act.GetWholesalersList): Observable<any> {
    ctx.patchState({loading: true, wholesalers: []});
    return this.http.get(endpoints.wholesalerIntegration.getAllWholesalers + '?limitToPharmacy=' + limitToPharmacy)
      .pipe(
        tap((responseData: any) => {
          ctx.patchState({
            wholesalers: responseData?.value?.wholesalers,
            wholesalerCredentials: responseData?.value?.wholesalerCredentials,
            loading: false,
          });
        }),
        catchError((err) => {
          ctx.patchState({loading: false});
          return of(null);
        }),
      );
  }

  @Action(Act.GetWholesalersFullList)
  getWholesalersFullList(ctx: StateContext<ProfileStateModel>, {}: Act.GetWholesalersFullList): Observable<any> {
    ctx.patchState({loading: true});
    return this.http.get(endpoints.wholesalerIntegration.getAllWholesalers)
      .pipe(
        tap((responseData: any) => {
          ctx.patchState({
            wholesalersFullList: responseData?.value?.wholesalers,
            loading: false,
          });
        }),
        catchError((err) => {
          ctx.patchState({loading: false});
          return of(null);
        }),
      );
  }

  @Action(Act.CreateWholesaler)
  createWholesaler(ctx: StateContext<ProfileStateModel>, {name}: Act.CreateWholesaler): Observable<any> {
    ctx.patchState({loading: true});
    return this.http.put(endpoints.wholesalerIntegration.createWholesaler, {name})
      .pipe(
        tap((responseData: any) => {
          ctx.patchState({
            loading: false,
          });
          if(responseData?.success){
            const wholesalers = [...ctx.getState().wholesalers];
            wholesalers.unshift({id: responseData?.value, name: name});
            ctx.patchState({
              wholesalers: wholesalers
            });
          }
        }),
        catchError((err) => {
          ctx.patchState({loading: false});
          return of(null);
        }),
      );
  }

  @Action(Act.GetAvailableWholesalersList)
  getAvailableWholesalersList(ctx: StateContext<ProfileStateModel>, {connectionId}: Act.GetAvailableWholesalersList): void {
    const currentPharmacyId = this.store.selectSnapshot(PharmacyState.currentPharmacy)?.id;
    const connections = ctx.getState().wholesalerIntegrations.filter(x => x?.pharmacyId === currentPharmacyId);
    ctx.patchState({
      currentPharmacyAvailableWholesalersList: ctx.getState().wholesalers
        .filter(x => connections.findIndex(y => y?.wholesalerId === x?.id && y?.id !== connectionId) === -1)
    });
  }

  @Action(Act.FilterWholesalers)
  filterWholesalers(ctx: StateContext<ProfileStateModel>, {token}: Act.FilterWholesalers): void {
    if(!token)
      return;
    const currentPharmacyId = this.store.selectSnapshot(PharmacyState.currentPharmacy)?.id;
    const connections = ctx.getState().wholesalerIntegrations.filter(x => x?.pharmacyId === currentPharmacyId);
    const connectionId = ctx.getState().pharmacySelectedWholesaler?.id;
    ctx.patchState({
      currentPharmacyAvailableWholesalersList: ctx.getState().wholesalers
        .filter(x => connections.findIndex(y => y?.wholesalerId === x?.id && y?.id !== connectionId) === -1)
        .filter(x => x.name.toLocaleLowerCase().includes(token.toLocaleLowerCase()))
    });
  }

  @Action(Act.SetPharmacySelectedWholesaler)
  setPharmacySelectedWholesaler(ctx: StateContext<ProfileStateModel>, {wholesaler}: Act.SetPharmacySelectedWholesaler): void {
    ctx.patchState({pharmacySelectedWholesaler: wholesaler});
  }

  @Action(Act.CloseRunningWholesalerMessage)
  closeRunningWholesalerMessage(ctx: StateContext<ProfileStateModel>, {}: Act.CloseRunningWholesalerMessage): void {
    ctx.patchState({runningWholesaler: undefined});
  }

  @Action(Act.RunWholesalerIntegration)
  runWholesalerIntegration(ctx: StateContext<ProfileStateModel>, {wholesaler}: Act.RunWholesalerIntegration): any {
    ctx.patchState({loading: true, runningWholesaler: wholesaler, runningResult: undefined});
    const pharmacyId = this.store.selectSnapshot(PharmacyState.currentPharmacy)?.id;
    return this.http.post(endpoints.wholesalerIntegration.runParsing, {wholesalerId: wholesaler.wholesalerId, pharmacyId})
      .pipe(
        tap((resp: any) => {
          if (resp.success) {
            ctx.patchState({
              wholesalerIntegrations: ctx.getState().wholesalerIntegrations
                .map(x => (x.id === resp.value.wholesalerIntegrationId ? {...x, lastProcessed: resp.value.lastProcessed} : x)),
              runningResult: RunningResultEnum.Success,
            });
          } else {
            ctx.patchState({
              runningResult: RunningResultEnum.Failed,
            });
          }

          ctx.patchState({
            loading: false,
          });
        }),
        catchError((err) => {
          ctx.patchState({loading: false, runningResult: RunningResultEnum.Failed});
          return of(null);
        }),
      );
  }

  @Action(Act.SetWholesalerConnectionActive)
  setWholesalerActive(ctx: StateContext<ProfileStateModel>, {id, setActive}: Act.SetWholesalerConnectionActive): Observable<any> {
    ctx.patchState({loading: true});
    return this.http.post(endpoints.wholesalerIntegration.updateActivity, {integrationId: id, isActive: setActive})
      .pipe(
        tap((resp: any) => {
          ctx.patchState({
            loading: false,
            wholesalerIntegrations: ctx.getState().wholesalerIntegrations.map(x => (x.id === id ? {...x, isActive: setActive} : x))
          });
        }),
        catchError((err) => {
          ctx.patchState({loading: false});
          return of(null);
        }),
      );
  }

  @Action(Act.RemoveWholesaler)
  removeWholesaler(ctx: StateContext<ProfileStateModel>, {id}: Act.RemoveWholesaler): Observable<any> {
    ctx.patchState({loading: true});
    return this.http.delete(endpoints.wholesalerIntegration.removeWholesaler + '?id=' + id)
      .pipe(
        tap((resp: any) => {
          ctx.patchState({
            loading: false,
          });
        }),
        catchError((err) => {
          ctx.patchState({loading: false});
          return of(null);
        }),
      );
  }

  @Action(Act.SubscribeToPharmacyManagementSystem)
  subscribePharmacyManagementSystemConnection(
    ctx: StateContext<ProfileStateModel>,
    {connectionData}: Act.SubscribeToPharmacyManagementSystem): Observable<any> {
    const pharmacyID = this.store.selectSnapshot(AuthState.pharmacyId);
    connectionData.pharmacyId = pharmacyID;

    ctx.patchState({loading: true, success: undefined});

    let path = '';
    switch (connectionData.integrationType) {
      case IntegrationTypeEnum.LibertyRx:
        path = endpoints.integration.liberty.addEdit;
        break;
      default:
        throw new Error('Unknown integration type');
    }

    return this.http.post(path, connectionData)
      .pipe(
        tap((resp: any) => {
          ctx.patchState({
            success: resp?.success ? true : false,
            loading: false,
          });
        }),
        catchError((err) => {
          ctx.patchState({loading: false});
          return of(null);
        }),
      );
  }

  @Action(Act.TestPharmacyManagementSystemConnection)
  testPharmacyManagementSystemConnection(
    ctx: StateContext<ProfileStateModel>,
    {connectionData}: Act.TestPharmacyManagementSystemConnection): Observable<any> {
    const pharmacyID = this.store.selectSnapshot(AuthState.pharmacyId);
    connectionData.pharmacyId = pharmacyID;

    ctx.patchState({loading: true, success: undefined, testPharmacyManagementSystemConnectionResponseStatusCode: undefined});

    let path = '';
    switch (connectionData.integrationType) {
      case IntegrationTypeEnum.LibertyRx:
        path = endpoints.integration.liberty.testConnection;
        break;
      default:
        throw new Error('Unknown integration type');
    }

    return this.http.post(path, connectionData)
      .pipe(
        tap((resp: any) => {
          ctx.patchState({
            success: resp?.success,
            loading: false,
            testPharmacyManagementSystemConnectionResponseStatusCode: resp?.code,
          });
        }),
        catchError((err) => {
          ctx.patchState({loading: false});
          return of(null);
        }),
      );
  }

  @Action(Act.TestWholesalerConnection)
  testWholesalerConnection(ctx: StateContext<ProfileStateModel>, {connectionData}: Act.TestWholesalerConnection): Observable<any> {
    ctx.patchState({loading: true, success: undefined});

    return this.http.post(endpoints.wholesalerIntegration.testConnection, connectionData)
      .pipe(
        tap((resp: any) => {
          ctx.patchState({
            success: resp?.success ? true : false,
            loading: false,
          });
        }),
        catchError((err) => {
          ctx.patchState({loading: false});
          return of(null);
        }),
      );
  }

  @Action(Act.SaveWholesalerConnection)
  saveWholesalerConnection(ctx: StateContext<ProfileStateModel>, {connectionData}: Act.SaveWholesalerConnection): Observable<any> {
    const pharmacyID = this.store.selectSnapshot(AuthState.pharmacyId);
    ctx.patchState({loading: true});
    connectionData.pharmacyId = pharmacyID;
    const wholesalerIntegrations = !ctx.getState().wholesalerIntegrations ? [] : ctx.getState().wholesalerIntegrations;
    return this.http.put(endpoints.wholesalerIntegration.create, connectionData)
      .pipe(
        tap((responseData: any) => {
          responseData.value.wholesalerName = ctx.getState().wholesalers.find(x => x.id === responseData?.value?.wholesalerId)?.name;
          ctx.patchState({
            loading: false,
            success: responseData.success,
            wholesalerIntegrations: [...wholesalerIntegrations, responseData?.value]
          });
        }),
        catchError((err) => {
          ctx.patchState({loading: false});
          return of(null);
        }),
      );
  }

  @Action(Act.SignWithPIN)
  signWithPIN(ctx: StateContext<ProfileStateModel>, {request}: Act.SignWithPIN): Observable<any> {
    ctx.patchState({loading: true, witnessError: false, success: null, response: null, witnessUserId: null});
    const userID = this.store.selectSnapshot(AuthState.activeUserId);
    const pharmacyID = this.store.selectSnapshot(PharmacyState.currentPharmacy).id;

    if (!request.isWitness) {
      ctx.patchState({amendingUserId: null});
    }

    return this.http.post(endpoints.sign.signWithPIN, {pharmacyID, userID, pin: request.pin})
      .pipe(
        tap((responseData: any) => {
          ctx.patchState({
            loading: false,
            response: responseData?.value,
            success: request.isWitness ?
              (!!responseData.value?.user.id && responseData.value.userID !== this.pinUserID) :
              !!responseData.value?.user.id,
            witnessError: request.isWitness && (!!responseData.value?.user.id && responseData.value.user.id === this.pinUserID),
          });

          if (!request.isWitness) {
            this.pinUserID = responseData.value?.user.id;
            ctx.patchState({amendingUserId: responseData.value?.user.id});
          } else ctx.patchState({witnessUserId: responseData.value?.user.id});
        }),
        catchError((err) => {
          ctx.patchState({loading: false});
          return of(null);
        }),
      );
  }

  @Action(Act.CheckPinValidity)
  checkPinValidity(ctx: StateContext<ProfileStateModel>, {pin}: Act.CheckPinValidity): Observable<any> {
    ctx.patchState({loading: true, success: null});
    const userID = this.store.selectSnapshot(AuthState.activeUserId);
    const pharmacyID = this.store.selectSnapshot(PharmacyState.currentPharmacy).id;

    return this.http.post(endpoints.sign.signWithPIN, {pharmacyID, userID, pin})
      .pipe(
        tap((responseData: any) => {
          if (responseData?.success) {
            this.store.dispatch(new SetActiveUser(responseData.value.user));
          }
          ctx.patchState({
            loading: false,
            success: !!responseData?.value?.user?.id,
          });
        }),
        catchError((err) => {
          ctx.patchState({loading: false});
          return of(null);
        }),
      );
  }

  @Action(Act.CheckPinBelongsToUser)
  checkPinBelongsToUser(ctx: StateContext<ProfileStateModel>, {pin}: Act.CheckPinBelongsToUser): Observable<any> {
    ctx.patchState({loading: true, success: null});
    const userID = this.store.selectSnapshot(AuthState.currentUser).id;
    const pharmacyID = this.store.selectSnapshot(PharmacyState.currentPharmacy).id;

    return this.http.post(endpoints.sign.signWithPIN, {pharmacyID, userID, pin})
      .pipe(
        tap((responseData: any) => {
          ctx.patchState({
            loading: false,
            success: responseData?.success && responseData?.value?.pinUserIsCurrentUser,
          });
        }),
        catchError((err) => {
          ctx.patchState({loading: false});
          return of(null);
        }),
      );
  }

  @Action(Act.SaveAccountPIN)
  saveAccountPIN(ctx: StateContext<ProfileStateModel>, {pin}: Act.SaveAccountPIN): Observable<any> {
    ctx.patchState({loading: true, success: null});
    const userID = this.store.selectSnapshot(AuthState.activeUserId);
    const pharmacyID = this.store.selectSnapshot(PharmacyState.currentPharmacy)?.id;

    return this.http.put(endpoints.account.updatePIN, {pharmacyID, userID, pin})
      .pipe(
        tap((responseData: any) => {

          if (responseData.code === 403) {
            ctx.patchState({
              loading: false,
              success: false,
            });
          } else {
            this.store.dispatch(new UserHasPin());
            ctx.patchState({
              loading: false,
              success: responseData.success
            });
          }
        }),
        catchError((err) => {
          ctx.patchState({loading: false, success: false});
          return of(null);
        }),
      );
  }

  @Action(Act.UpdateAccountPIN)
  updateAccountPIN(ctx: StateContext<ProfileStateModel>, {pin}: Act.UpdateAccountPIN): Observable<any> {
    ctx.patchState({loading: true, success: null});
    const userID = this.store.selectSnapshot(AuthState.activeUserId);
    const pharmacyID = this.store.selectSnapshot(PharmacyState.currentPharmacy).id;
    return this.http.put(endpoints.account.updatePIN, {pharmacyID, userID, pin})
      .pipe(
        tap((resp: any) => {
          if (resp.code === 403) {
            ctx.patchState({
              loading: false,
              success: false,
            });
          } else {
            ctx.patchState({
              loading: false,
              success: resp.success,
            });
          }
        }),
        catchError((err) => {
          ctx.patchState({loading: false, success: false});
          return of(null);
        }),
      );
  }

  @Action(Act.SendInvitation)
  sendInvitation(ctx: StateContext<ProfileStateModel>, {users}: Act.SendInvitation): Observable<any> {
    const pharmacyID = this.store.selectSnapshot(AuthState.pharmacyId);

    ctx.patchState({loading: true, failedInvitationEmails: [], success: null});
    users = users.map(obj => ({...obj, pharmacyId: pharmacyID}));
    return this.http.put(endpoints.user.createUser, users)
      .pipe(
        tap((responseData: any) => {
          ctx.patchState({
            loading: false,
            success: responseData?.success,
            failedInvitationEmails: responseData.value?.failedInvitationEmails || [],
          });
        }),
        catchError((err) => {
          ctx.patchState({loading: false});
          return of(null);
        }),
      );
  }

  @Action(Act.EditTeammate)
  editTeammate(ctx: StateContext<ProfileStateModel>, {user}: Act.EditTeammate): Observable<any> {
    ctx.patchState({loading: true, editUserResponse: null, success: null});

    return this.http.patch(endpoints.user.updateUser, user)
      .pipe(
        tap((responseData: any) => {
          ctx.patchState({
            loading: false,
            success: responseData?.success,
            editUserResponse: responseData
          });

          if (responseData && !responseData.success) {
            ctx.patchState({editUserResponse: responseData});
          }
        }),
        catchError((err) => {
          ctx.patchState({loading: false});
          return of(null);
        }),
      );
  }

  @Action(Act.ResendUserInvitation)
  resendUserInvitation(ctx: StateContext<ProfileStateModel>, {email}: Act.ResendUserInvitation): Observable<any> {
    const pharmacyId = this.store.selectSnapshot(AuthState.pharmacyId);

    ctx.patchState({loading: true, success: null});
    return this.http.post(endpoints.user.resendInvitation, {email, pharmacyId})
      .pipe(
        tap((responseData: any) => {
          ctx.patchState({
            loading: false,
            success: responseData?.success,
          });
        }),
        catchError((err) => {
          ctx.patchState({loading: false});
          return of(null);
        }),
      );
  }

  @Action(Act.RevokeUserInvitation)
  revokeUserInvitation(ctx: StateContext<ProfileStateModel>, {email}: Act.RevokeUserInvitation): Observable<any> {
    ctx.patchState({loading: true, success: null});
    const emailEncoded = encodeURIComponent(email);
    return this.http.get(`${endpoints.user.revokeInvite}?email=${emailEncoded}`)
      .pipe(
        tap((responseData: any) => {
          ctx.patchState({
            loading: false,
            success: responseData?.success,
          });
        }),
        catchError((err) => {
          ctx.patchState({loading: false});
          return of(null);
        }),
      );
  }

  @Action(Act.DeleteUser)
  deleteUser(ctx: StateContext<ProfileStateModel>, {email}: Act.DeleteUser): Observable<any> {
    ctx.patchState({loading: true, success: null});
    const emailEncoded = encodeURIComponent(email);
    return this.http.delete(`${endpoints.user.revokeInvite}?email=${emailEncoded}`)
      .pipe(
        tap((responseData: any) => {
          ctx.patchState({
            loading: false,
            success: responseData?.success,
          });
        }),
        catchError((err) => {
          ctx.patchState({loading: false});
          return of(null);
        }),
      );
  }

  @Action(Act.UpdateUser)
  updateUser(ctx: StateContext<ProfileStateModel>, {user}: Act.UpdateUser): Observable<any> {
    ctx.patchState({loading: true});

    return this.http.patch(endpoints.account.updateAccount, user)
      .pipe(
        tap((resp: any) => {
          ctx.patchState({
            loading: false,
            editUserResponse: resp
          });
        }),
        catchError((err) => {
          ctx.patchState({loading: false});
          return of(null);
        }),
        mergeMap(() => this.store.dispatch(new UpdateUserAccount(user))),
      );
  }

  @Action(Act.GetBeaconSignature)
  getBeaconSignature(ctx: StateContext<ProfileStateModel>, {}: Act.GetBeaconSignature): Observable<any> {
    ctx.patchState({loading: true, beaconSignature: null});
    return this.http.get(endpoints.user.getBeaconSignature + '/' + this.store.selectSnapshot(AuthState.activeUser).email)
      .pipe(
        tap((resp: any) => {
          ctx.patchState({
            loading: false,
            beaconSignature: resp?.value
          });
        }),
        catchError((err) => {
          ctx.patchState({loading: false});
          return of(null);
        }),
      );
  }
}
